import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import QuoteFormModal from '../components/QuoteFormModal';
import SEO from '../components/SEO';
import { breadcrumbSchema, productSchema } from '../lib/seoConstants';
import { useReveal } from '../hooks/useReveal';
import { REO_CONTENT } from '../lib/realEstateContent';
import {
  ArrowLeft, ArrowRight, MapPin, Building2, Home, Users, Handshake,
  Wrench, LineChart, ShieldCheck, Check, Compass, FileText, Sparkles,
} from 'lucide-react';

// Curated luxury real estate imagery (warm cinematic tones, no random building shots)
const IMG = {
  exterior: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=80&auto=format&fit=crop',
  lobby: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1600&q=80&auto=format&fit=crop',
  living: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80&auto=format&fit=crop',
  commercial: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80&auto=format&fit=crop',
};

// Reusable section wrapper
const Reveal = ({ children, className = '', delay = 0 }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
    >
      {children}
    </div>
  );
};

const RealEstateOSPage = () => {
  const { language, isRTL } = useLanguage();
  const [showQuote, setShowQuote] = useState(false);
  const lang = language === 'ar' ? 'ar' : 'en';
  const c = REO_CONTENT[lang];
  const dir = isRTL ? 'rtl' : 'ltr';
  const fontFamily = isRTL ? "'Cairo', 'Tajawal', sans-serif" : "'DM Sans', 'Inter', sans-serif";
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const BackArrow = isRTL ? ArrowRight : ArrowLeft;

  const openQuote = () => setShowQuote(true);
  const scrollToJourney = () => document.getElementById('reo-journey')?.scrollIntoView({ behavior: 'smooth' });

  // Fake product-like object for schema.org
  const productForSchema = {
    slug: c.slug,
    name_ar: REO_CONTENT.ar.productName,
    name_en: REO_CONTENT.en.productName,
    short_desc_ar: REO_CONTENT.ar.hero.text,
    short_desc_en: REO_CONTENT.en.hero.text,
    image: IMG.exterior,
  };

  return (
    <div dir={dir} style={{ fontFamily }} data-testid="reo-page">
      <SEO
        path={`/products/${c.slug}`}
        type="product"
        titleEn={REO_CONTENT.en.seo.title}
        titleAr={REO_CONTENT.ar.seo.title}
        descriptionEn={REO_CONTENT.en.seo.description}
        descriptionAr={REO_CONTENT.ar.seo.description}
        image={IMG.exterior}
        schemas={[
          productSchema(productForSchema, lang),
          breadcrumbSchema([
            { name: lang === 'ar' ? 'الرئيسية' : 'Home', url: '/' },
            { name: lang === 'ar' ? 'المنتجات' : 'Products', url: '/products' },
            { name: c.productName, url: `/products/${c.slug}` },
          ]),
        ]}
      />
      <Header />

      {/* ===== HERO (dark) ===== */}
      <section className="relative pt-24 pb-20 bg-gradient-to-br from-[#0A1628] via-[#0E2036] to-[#0A1628] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={IMG.exterior} alt={lang === 'ar' ? 'واجهة مشروع سكني حديث — بيانات توضيحية' : 'Modern residential building exterior — illustrative'} className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link to="/products" className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-8 transition-colors" data-testid="reo-back-btn">
            <BackArrow className="w-4 h-4" /> {c.backToProducts}
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.3em] text-[#00B4D8] mb-4">{c.hero.overline}</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
                {c.hero.title}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">{c.hero.text}</p>
              <div className="mt-9 flex flex-wrap gap-3">
                <button data-testid="reo-hero-cta-primary" onClick={openQuote}
                  className="inline-flex items-center gap-2 bg-[#00B4D8] hover:bg-[#0094B3] text-white font-semibold px-7 py-3.5 rounded-lg transition-colors text-sm">
                  {c.hero.cta1} <Arrow className="w-4 h-4" />
                </button>
                <button data-testid="reo-hero-cta-secondary" onClick={scrollToJourney}
                  className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 hover:bg-white/5 text-white font-medium px-7 py-3.5 rounded-lg transition-colors text-sm">
                  {c.hero.cta2}
                </button>
              </div>
            </div>

            {/* Interface mockup: mini map + unit chip */}
            <div className="lg:col-span-5">
              <div className="bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl">
                <div className="flex items-center gap-2 text-xs text-slate-300 mb-3">
                  <MapPin className="w-3.5 h-3.5 text-[#00B4D8]" />
                  <span>{c.hero.chipMap}</span>
                </div>
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-slate-800 relative">
                  <iframe
                    title="Al Narjis illustrative map"
                    src="https://www.openstreetmap.org/export/embed.html?bbox=46.6650%2C24.8290%2C46.6870%2C24.8440&layer=mapnik&marker=24.8367%2C46.6753"
                    className="w-full h-full border-0 grayscale-[0.2] contrast-[0.95]"
                    loading="lazy"
                  />
                </div>
                <div className="mt-4 flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                  <div>
                    <p className="text-xs text-slate-400">{c.hero.chipUnit}</p>
                    <p className="text-sm font-semibold text-white">A · 2F · 148 m²</p>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 bg-emerald-400/10 px-2.5 py-1 rounded-full">
                    {c.hero.chipStatus}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 2. PROBLEM ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C6A3E] mb-3">{c.problem.overline}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2432] leading-tight max-w-3xl">{c.problem.title}</h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-3xl">{c.problem.lead}</p>
          </Reveal>

          {/* Connected chain of 5 items */}
          <Reveal delay={150} className="mt-14">
            <div className="relative flex flex-wrap items-center justify-between gap-4 md:gap-2">
              <div className="hidden md:block absolute top-1/2 start-0 end-0 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent -z-0" />
              {c.problem.chain.map((item, i) => (
                <div key={i} className="relative z-10 flex flex-col items-center text-center w-full md:w-auto md:flex-1">
                  <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center mb-2 shadow-sm">
                    <span className="text-xs font-bold text-slate-500">{i + 1}</span>
                  </div>
                  <p className="text-sm text-slate-700 font-medium max-w-[160px]">{item}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={250} className="mt-14">
            <div className="border-s-4 border-[#3F6E52] ps-6 py-2 bg-[#F5F7F4] rounded-e-lg">
              <p className="text-lg sm:text-xl font-bold text-[#1B2432]">{c.problem.punchline}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== 3. CORE IDEA — JOURNEY ===== */}
      <section id="reo-journey" className="py-24 bg-[#F8F6F1]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C6A3E] mb-3">{c.idea.overline}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2432] leading-tight max-w-3xl">{c.idea.title}</h2>
          </Reveal>

          <Reveal delay={150} className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <ol className="space-y-3">
                {c.idea.journey.map((step, i) => (
                  <li key={i} className="flex items-center gap-4 p-4 bg-white border border-slate-100 rounded-xl">
                    <span className="w-8 h-8 rounded-full bg-[#3F6E52]/10 text-[#3F6E52] flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-sm sm:text-base font-medium text-[#1B2432]">{step}</span>
                    {i < c.idea.journey.length - 1 && (
                      <Arrow className="w-4 h-4 text-slate-300 ms-auto" />
                    )}
                  </li>
                ))}
              </ol>
              <p className="mt-8 text-base text-slate-600 leading-relaxed">{c.idea.text}</p>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-xl aspect-[4/5]">
                <img src={IMG.lobby} alt={lang === 'ar' ? 'ردهة استقبال أنيقة — بيانات توضيحية' : 'Elegant reception lobby — illustrative'} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== 4. SEARCH & UNIT SELECTION ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C6A3E] mb-3">{c.search.overline}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2432] leading-tight max-w-3xl">{c.search.title}</h2>
          </Reveal>

          <Reveal delay={150} className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Real OpenStreetMap embed */}
            <div className="bg-[#F8F6F1] border border-slate-100 rounded-2xl overflow-hidden">
              <div className="p-4 flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-slate-600 font-medium">
                  <MapPin className="w-3.5 h-3.5 text-[#3F6E52]" /> {c.search.mapLabel}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-slate-400">{c.search.mapCaption}</span>
              </div>
              <div className="aspect-[4/3]">
                <iframe
                  title="Al Narjis Riyadh map"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=46.6650%2C24.8290%2C46.6870%2C24.8440&layer=mapnik&marker=24.8367%2C46.6753"
                  className="w-full h-full border-0"
                  loading="lazy"
                />
              </div>
            </div>

            {/* SVG floorplate with interactive unit */}
            <FloorplateSVG unit={c.search.unit} hint={c.search.hint} hintMobile={c.search.hintMobile} lang={lang} />
          </Reveal>
        </div>
      </section>

      {/* ===== 5. INQUIRY WITH CONTEXT ===== */}
      <section className="py-24 bg-[#F8F6F1]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C6A3E] mb-3">{c.inquiry.overline}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2432] leading-tight max-w-3xl">{c.inquiry.title}</h2>
          </Reveal>

          <Reveal delay={150} className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 bg-white border border-slate-100 rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-2 mb-5 text-xs text-[#3F6E52] font-semibold uppercase tracking-widest">
                <FileText className="w-3.5 h-3.5" />
                <span>{lang === 'ar' ? 'سجل استفسار' : 'Inquiry record'}</span>
              </div>
              <dl className="divide-y divide-slate-100">
                {c.inquiry.fields.map((f, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-3">
                    <dt className="text-xs text-slate-500 uppercase tracking-wider">{f.label}</dt>
                    <dd className="sm:col-span-2 text-sm text-[#1B2432] font-medium">{f.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="lg:col-span-2">
              <h3 className="text-lg font-bold text-[#1B2432] mb-4">{c.inquiry.valueTitle}</h3>
              <ul className="space-y-3">
                {c.inquiry.values.map((v, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 bg-white border border-slate-100 rounded-lg">
                    <Check className="w-4 h-4 text-[#3F6E52] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{v}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== 6. SALES & LEASING TRACKS ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C6A3E] mb-3">{c.tracks.overline}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2432] leading-tight max-w-3xl">{c.tracks.title}</h2>
          </Reveal>

          <Reveal delay={150} className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[c.tracks.sale, c.tracks.lease].map((track, ti) => (
              <div key={ti} className="bg-[#F8F6F1] border border-slate-100 rounded-2xl p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-6">
                  <div className={`w-2 h-2 rounded-full ${ti === 0 ? 'bg-[#3F6E52]' : 'bg-[#8C6A3E]'}`} />
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-600">{track.label}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {track.steps.map((s, i) => (
                    <React.Fragment key={i}>
                      <span className="text-sm font-medium text-[#1B2432] bg-white border border-slate-200 px-3 py-2 rounded-lg">{s}</span>
                      {i < track.steps.length - 1 && <Arrow className="w-3.5 h-3.5 text-slate-300" />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </Reveal>

          <Reveal delay={250} className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="rounded-2xl overflow-hidden aspect-[16/9]">
                <img src={IMG.commercial} alt={lang === 'ar' ? 'مساحة تجارية داخلية — بيانات توضيحية' : 'Commercial interior space — illustrative'} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
            <p className="lg:col-span-5 text-base text-slate-600 leading-relaxed">{c.tracks.caption}</p>
          </Reveal>
        </div>
      </section>

      {/* ===== 7. PROPERTY MANAGEMENT ===== */}
      <section className="py-24 bg-[#F8F6F1]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C6A3E] mb-3">{c.property.overline}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2432] leading-tight max-w-3xl">{c.property.title}</h2>
          </Reveal>

          <Reveal delay={150} className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.property.items.map((item, i) => (
              <div key={i} className="bg-white border border-slate-100 rounded-xl p-6 hover:border-[#3F6E52]/30 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-lg bg-[#3F6E52]/10 flex items-center justify-center mb-4">
                  <Building2 className="w-5 h-5 text-[#3F6E52]" />
                </div>
                <h3 className="text-base font-bold text-[#1B2432] mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </Reveal>

          <Reveal delay={250} className="mt-10 max-w-3xl">
            <p className="text-base text-slate-600 leading-relaxed">{c.property.text}</p>
          </Reveal>
        </div>
      </section>

      {/* ===== 8. BROKERS ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C6A3E] mb-3">{c.brokers.overline}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2432] leading-tight max-w-3xl">{c.brokers.title}</h2>
          </Reveal>

          <Reveal delay={150} className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <ul className="space-y-3">
              {c.brokers.items.map((it, i) => (
                <li key={i} className="flex items-start gap-3 p-4 bg-[#F8F6F1] rounded-lg">
                  <Handshake className="w-4 h-4 text-[#8C6A3E] flex-shrink-0 mt-1" />
                  <span className="text-sm text-slate-700">{it}</span>
                </li>
              ))}
            </ul>
            <div className="bg-[#1B2432] text-white rounded-2xl p-8">
              <ShieldCheck className="w-8 h-8 text-[#00B4D8] mb-4" />
              <p className="text-lg font-semibold leading-relaxed">{c.brokers.punch}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== 9. MAINTENANCE ===== */}
      <section className="py-24 bg-[#F8F6F1]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C6A3E] mb-3">{c.maintenance.overline}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2432] leading-tight max-w-3xl">{c.maintenance.title}</h2>
          </Reveal>

          <Reveal delay={150} className="mt-14">
            <div className="bg-white border border-slate-100 rounded-2xl p-6 sm:p-8">
              <div className="flex flex-wrap items-center gap-3">
                {c.maintenance.steps.map((s, i) => (
                  <React.Fragment key={i}>
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-[#F8F6F1] border border-slate-200 rounded-lg">
                      <Wrench className="w-3.5 h-3.5 text-[#8C6A3E]" />
                      <span className="text-sm font-medium text-[#1B2432]">{s}</span>
                    </div>
                    {i < c.maintenance.steps.length - 1 && <Arrow className="w-3.5 h-3.5 text-slate-300" />}
                  </React.Fragment>
                ))}
              </div>
              <p className="mt-8 text-base text-slate-600 leading-relaxed max-w-3xl">{c.maintenance.text}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== 10. FOUR EXPERIENCES ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C6A3E] mb-3">{c.experiences.overline}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2432] leading-tight max-w-3xl">{c.experiences.title}</h2>
          </Reveal>

          <Reveal delay={150} className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {c.experiences.items.map((it, i) => (
              <div key={i} className="p-6 bg-[#F8F6F1] border border-slate-100 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 rounded-full bg-[#3F6E52]/10 text-[#3F6E52] flex items-center justify-center text-sm font-bold">{i + 1}</span>
                  <h3 className="text-base font-bold text-[#1B2432]">{it.role}</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ===== 11. VALUE ===== */}
      <section className="py-24 bg-[#F8F6F1]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C6A3E] mb-3">{c.value.overline}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2432] leading-tight max-w-3xl">{c.value.title}</h2>
          </Reveal>

          <Reveal delay={150} className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
            {c.value.items.map((it, i) => (
              <div key={i} className="p-6 bg-white border border-slate-100 rounded-xl flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#8C6A3E]/10 flex items-center justify-center flex-shrink-0">
                  <LineChart className="w-5 h-5 text-[#8C6A3E]" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1B2432] mb-1.5">{it.role}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ===== 12. WHY NEXA ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C6A3E] mb-3">{c.why.overline}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2432] leading-tight max-w-3xl">{c.why.title}</h2>
          </Reveal>

          <Reveal delay={150} className="mt-12 space-y-3">
            {c.why.pairs.map(([before, after], i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-11 gap-3 items-stretch">
                <div className="md:col-span-5 p-4 sm:p-5 bg-slate-50 border border-slate-100 rounded-xl text-slate-500 text-sm line-through decoration-slate-300">
                  {before}
                </div>
                <div className="hidden md:flex md:col-span-1 items-center justify-center">
                  <Arrow className="w-5 h-5 text-[#00B4D8]" />
                </div>
                <div className="md:col-span-5 p-4 sm:p-5 bg-[#F8F6F1] border border-[#3F6E52]/20 rounded-xl">
                  <div className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#3F6E52] flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-medium text-[#1B2432]">{after}</span>
                  </div>
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ===== 13. METHOD ===== */}
      <section className="py-24 bg-[#F8F6F1]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C6A3E] mb-3">{c.method.overline}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2432] leading-tight max-w-3xl">{c.method.title}</h2>
          </Reveal>

          <Reveal delay={150} className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {c.method.phases.map((p, i) => (
              <div key={i} className="p-6 bg-white border border-slate-100 rounded-xl">
                <div className="text-xs font-bold text-[#8C6A3E] uppercase tracking-widest mb-3">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3 className="text-base font-bold text-[#1B2432] mb-2">{p.name}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </Reveal>

          <Reveal delay={300} className="mt-10 max-w-3xl">
            <div className="p-5 bg-white border-s-4 border-[#00B4D8] rounded-e-lg">
              <p className="text-sm text-slate-600 leading-relaxed">{c.method.note}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== 14. FINAL CTA (dark) ===== */}
      <section className="py-24 bg-gradient-to-br from-[#0A1628] via-[#0E2036] to-[#0A1628] text-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <Reveal>
            <Compass className="w-10 h-10 text-[#00B4D8] mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight">{c.finalCta.title}</h2>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">{c.finalCta.text}</p>
            <button data-testid="reo-final-cta" onClick={openQuote}
              className="mt-9 inline-flex items-center gap-2 bg-[#00B4D8] hover:bg-[#0094B3] text-white font-semibold px-8 py-4 rounded-lg transition-colors text-sm">
              {c.finalCta.cta} <Arrow className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>

      {showQuote && <QuoteFormModal productName={c.productName} onClose={() => setShowQuote(false)} />}
      <Footer />
    </div>
  );
};

// ===== Floorplate SVG with interactive unit A-201 =====
const FloorplateSVG = ({ unit, hint, hintMobile, lang }) => {
  const [hovered, setHovered] = useState(false);
  const [touched, setTouched] = useState(false);
  const active = hovered || touched;

  return (
    <div className="bg-[#F8F6F1] border border-slate-100 rounded-2xl overflow-hidden">
      <div className="p-4 flex items-center justify-between text-xs">
        <span className="text-slate-600 font-medium">{lang === 'ar' ? 'مخطط الطابق الثاني — المبنى A' : 'Floor plan — Building A, Level 2'}</span>
        <span className="text-[10px] uppercase tracking-widest text-slate-400 hidden sm:inline">{hint}</span>
        <span className="text-[10px] uppercase tracking-widest text-slate-400 sm:hidden">{hintMobile}</span>
      </div>
      <div className="aspect-[4/3] relative bg-white">
        <svg viewBox="0 0 400 300" className="w-full h-full" aria-label={lang === 'ar' ? 'مخطط توضيحي للطابق' : 'Illustrative floor plan'}>
          {/* Outer walls */}
          <rect x="20" y="20" width="360" height="260" fill="none" stroke="#1B2432" strokeWidth="3" />
          {/* Corridor */}
          <rect x="20" y="140" width="360" height="30" fill="#F8F6F1" stroke="#94A3B8" strokeWidth="1" />
          <text x="200" y="160" textAnchor="middle" fontSize="9" fill="#94A3B8" fontFamily="sans-serif">{lang === 'ar' ? 'ممر' : 'CORRIDOR'}</text>
          {/* Units row top: A-202, A-201 (target), A-203 */}
          <rect x="20" y="20" width="115" height="120" fill="#F1F5F9" stroke="#94A3B8" strokeWidth="1" />
          <text x="77.5" y="85" textAnchor="middle" fontSize="12" fill="#64748B" fontFamily="sans-serif">A-202</text>
          {/* A-201 target */}
          <g
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => setTouched(t => !t)}
            style={{ cursor: 'pointer' }}
          >
            <rect x="135" y="20" width="130" height="120"
              fill={active ? '#3F6E52' : '#DFE9E1'}
              stroke={active ? '#1B2432' : '#3F6E52'}
              strokeWidth={active ? '3' : '2'}
              style={{ transition: 'fill 200ms, stroke 200ms' }}
            />
            <text x="200" y="80" textAnchor="middle" fontSize="14" fontWeight="bold" fill={active ? '#FFFFFF' : '#1B2432'} fontFamily="sans-serif">A-201</text>
            <text x="200" y="100" textAnchor="middle" fontSize="10" fill={active ? '#FFFFFF' : '#3F6E52'} fontFamily="sans-serif">{unit.area}</text>
          </g>
          <rect x="265" y="20" width="115" height="120" fill="#F1F5F9" stroke="#94A3B8" strokeWidth="1" />
          <text x="322.5" y="85" textAnchor="middle" fontSize="12" fill="#64748B" fontFamily="sans-serif">A-203</text>
          {/* Bottom row */}
          <rect x="20" y="170" width="115" height="110" fill="#F1F5F9" stroke="#94A3B8" strokeWidth="1" />
          <text x="77.5" y="230" textAnchor="middle" fontSize="12" fill="#64748B" fontFamily="sans-serif">A-204</text>
          <rect x="135" y="170" width="130" height="110" fill="#F1F5F9" stroke="#94A3B8" strokeWidth="1" />
          <text x="200" y="230" textAnchor="middle" fontSize="12" fill="#64748B" fontFamily="sans-serif">A-205</text>
          <rect x="265" y="170" width="115" height="110" fill="#F1F5F9" stroke="#94A3B8" strokeWidth="1" />
          <text x="322.5" y="230" textAnchor="middle" fontSize="12" fill="#64748B" fontFamily="sans-serif">A-206</text>
          {/* N compass */}
          <g transform="translate(360,40)">
            <text x="0" y="0" textAnchor="middle" fontSize="9" fill="#64748B" fontFamily="sans-serif">N</text>
            <line x1="0" y1="3" x2="0" y2="13" stroke="#64748B" strokeWidth="1" />
            <polygon points="0,0 -3,6 3,6" fill="#64748B" />
          </g>
        </svg>

        {/* Info popup when active */}
        {active && (
          <div className="absolute top-4 end-4 bg-white border border-slate-200 rounded-lg shadow-xl p-4 w-56 z-10 animate-in fade-in duration-200"
            data-testid="unit-info-popup">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-[#1B2432]">{unit.code}</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{unit.status}</span>
            </div>
            <dl className="space-y-1.5 text-xs">
              <div className="flex justify-between"><dt className="text-slate-500">{lang === 'ar' ? 'الغرف' : 'Bedrooms'}</dt><dd className="text-slate-800 font-medium">{unit.rooms}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">{lang === 'ar' ? 'المساحة' : 'Area'}</dt><dd className="text-slate-800 font-medium">{unit.area}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">{lang === 'ar' ? 'الاتجاه' : 'Direction'}</dt><dd className="text-slate-800 font-medium">{unit.direction}</dd></div>
              <div className="flex justify-between pt-1.5 border-t border-slate-100"><dt className="text-slate-500">{lang === 'ar' ? 'السعر' : 'Price'}</dt><dd className="text-[#3F6E52] font-bold">{unit.price}</dd></div>
            </dl>
          </div>
        )}
      </div>
    </div>
  );
};

export default RealEstateOSPage;
